"use client"

import { useForm } from "@inertiajs/react"
import { Edit02 } from "@untitledui/icons"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { ButtonUtility } from "@/components/ui/button-utility"
import { HintText, InputBase, Label, TextField } from "@/components/ui/input"
import { SlideoutMenu } from "@/components/ui/slideout-menu"
import { Toggle } from "@/components/ui/toggle"
import { playlistTrackPath } from "@/routes"
import { ContentBodyTypeEnum, type Tag } from "@/types"

// The slideout and the standalone track form share the exact same field
// components and helpers, so a track edits identically wherever it's opened.
import ContentBodyForms, {
  type TrackFormData,
} from "../../tracks/_components/content-body-forms"
import ContentTypeSelector from "../../tracks/_components/content-type-selector"
import TagFields from "../../tracks/_components/tag-fields"
import { mergeContentBodyAttributes } from "../../tracks/_helpers/merge-content-body-attributes"
import { setPrefillValues } from "../../tracks/_helpers/set-prefill-value"
import { useUrlPrefill } from "../../tracks/_hooks/use-url-prefill"
import { contentBodyAttributes } from "../../tracks/tracks.constant"

import type { ClaimTrack, TrackPersistParams } from "./claim-track-card"

interface ClaimTrackEditSlideoutProps {
  track: ClaimTrack
  /** Playlist slug or id, for the track endpoint. */
  pid: number | string
  persistParams: TrackPersistParams
  availableTags: Tag[]
}

/**
 * "Edit details" for a track, in a slideout — the full track editor without
 * leaving the manuscript. It shares its field components (content-type picker,
 * per-type content body forms, URL prefill, tags) with the standalone
 * tracks/edit form, so the two surfaces can't drift. The "why it matters" note
 * is edited inline on the card, so it is deliberately absent here.
 */
export function ClaimTrackEditSlideout({
  track,
  pid,
  persistParams,
  availableTags,
}: ClaimTrackEditSlideoutProps) {
  const seedTags = (track.tags as Tag[] | undefined) ?? []

  const seed = (): TrackFormData["track"] => {
    const post = track.post
    const type =
      (post.content_body_type as ContentBodyTypeEnum | null) ??
      ContentBodyTypeEnum.video
    return {
      notes: track.notes ?? "",
      tag_ids: seedTags.map((t) => t.id),
      post_attributes: {
        id: post.id,
        title: post.title,
        creator: post.creator ?? "",
        canonical_url: post.canonical_url ?? "",
        thumbnail_url: post.thumbnail_url ?? "",
        published: post.published,
        content_body_type: type,
        content_body_attributes: mergeContentBodyAttributes(
          post.content_body_attributes,
          type,
        ),
      },
    }
  }

  const form = useForm<TrackFormData>({ track: seed() })
  const { data, setData, processing, errors, clearErrors } = form

  // Reseed when the track prop changes (a save reloads the editor) using the
  // repo's "adjust state when a prop changes" pattern, so reopening shows the
  // freshly-saved values rather than a stale form.
  const [prevTrack, setPrevTrack] = useState(track)
  if (track !== prevTrack) {
    setPrevTrack(track)
    setData("track", seed())
    clearErrors()
  }

  const err = errors as unknown as Record<string, string | undefined>
  const p = data.track.post_attributes
  const setPost = (next: Partial<typeof p>) =>
    setData("track", { ...data.track, post_attributes: { ...p, ...next } })

  const changeType = (next: ContentBodyTypeEnum) =>
    setData("track", {
      ...data.track,
      post_attributes: {
        ...p,
        content_body_type: next,
        content_body_attributes: { ...contentBodyAttributes[next] },
      },
    })

  // Editing the URL re-unfurls and prefills the fields the lookup resolves; a
  // seeded URL doesn't auto-fetch, so opening the slideout never re-fetches.
  const { loading: lookingUp } = useUrlPrefill({
    url: p.canonical_url,
    onResult: (result) =>
      setData("track", {
        ...data.track,
        post_attributes: setPrefillValues(result, p),
      }),
  })

  const save = (close: () => void) => {
    const hasImageFile = p.content_body_attributes.image instanceof File
    // The inline note is edited on the card, not here — strip it so a save of
    // the details never reverts an in-flight note edit.
    form.transform((d) => ({
      track: {
        tag_ids: d.track.tag_ids,
        post_attributes: d.track.post_attributes,
      },
    }))
    form.patch(
      playlistTrackPath(pid, track.id, {
        ...persistParams,
        content_type: p.content_body_type,
      }),
      { forceFormData: hasImageFile, preserveScroll: true, onSuccess: close },
    )
  }

  const formId = `track-edit-${track.id}`

  return (
    <SlideoutMenu.Trigger>
      <ButtonUtility
        size="xs"
        color="tertiary"
        tooltip="Edit details"
        icon={Edit02}
      />
      <SlideoutMenu>
        {({ close }: { close: () => void }) => (
          <>
            <SlideoutMenu.Header onClose={close}>
              <div className="flex flex-col gap-0.5 pr-10">
                <p className="text-primary text-lg font-semibold">
                  Edit track details
                </p>
                <p className="text-tertiary text-sm">
                  Your note stays on the card — this is everything else.
                </p>
              </div>
            </SlideoutMenu.Header>

            <SlideoutMenu.Content>
              <form
                id={formId}
                onSubmit={(e) => {
                  e.preventDefault()
                  save(close)
                }}
                className="flex flex-col gap-5"
              >
                <ContentTypeSelector
                  value={p.content_body_type}
                  onChange={changeType}
                />

                <TextField
                  isRequired
                  value={p.canonical_url}
                  onChange={(v) => setPost({ canonical_url: v })}
                  isInvalid={!!err["track.post_attributes.canonical_url"]}
                >
                  <Label>Content URL</Label>
                  <InputBase size="md" placeholder="https://..." />
                  {lookingUp ? (
                    <HintText>Looking up this link…</HintText>
                  ) : (
                    err["track.post_attributes.canonical_url"] && (
                      <HintText isInvalid>
                        {err["track.post_attributes.canonical_url"]}
                      </HintText>
                    )
                  )}
                </TextField>

                <TextField
                  isRequired
                  value={p.title}
                  onChange={(v) => setPost({ title: v })}
                  isInvalid={!!err["track.post_attributes.title"]}
                >
                  <Label>Title</Label>
                  <InputBase size="md" placeholder="The title of the content" />
                  {err["track.post_attributes.title"] && (
                    <HintText isInvalid>
                      {err["track.post_attributes.title"]}
                    </HintText>
                  )}
                </TextField>

                <TextField
                  value={p.creator}
                  onChange={(v) => setPost({ creator: v })}
                  isInvalid={!!err["track.post_attributes.creator"]}
                >
                  <Label>Creator</Label>
                  <InputBase size="md" placeholder="Creator name" />
                  {err["track.post_attributes.creator"] && (
                    <HintText isInvalid>
                      {err["track.post_attributes.creator"]}
                    </HintText>
                  )}
                </TextField>

                <TextField
                  value={p.thumbnail_url}
                  onChange={(v) => setPost({ thumbnail_url: v })}
                  isInvalid={!!err["track.post_attributes.thumbnail_url"]}
                >
                  <Label>Thumbnail URL</Label>
                  <InputBase size="md" placeholder="https://..." />
                  {err["track.post_attributes.thumbnail_url"] && (
                    <HintText isInvalid>
                      {err["track.post_attributes.thumbnail_url"]}
                    </HintText>
                  )}
                </TextField>

                <ContentBodyForms
                  data={data}
                  setData={setData}
                  errors={errors}
                />

                <TagFields
                  availableTags={availableTags}
                  initialSelectedTags={seedTags}
                  tagIds={data.track.tag_ids}
                  onTagIdsChange={(ids) =>
                    setData("track", { ...data.track, tag_ids: ids })
                  }
                  error={err["track.tag_ids"]}
                />

                <Toggle
                  isSelected={p.published}
                  onChange={(isSelected) => setPost({ published: isSelected })}
                  size="md"
                  hint={
                    p.published
                      ? "This track is published and visible to everyone."
                      : "This track is unpublished and only visible to you."
                  }
                  label={p.published ? "Published" : "Unpublished"}
                  slim
                />
              </form>
            </SlideoutMenu.Content>

            <SlideoutMenu.Footer>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  color="secondary"
                  size="md"
                  onClick={close}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  color="primary"
                  size="md"
                  isDisabled={processing}
                  isLoading={processing}
                  onClick={() => save(close)}
                >
                  Save changes
                </Button>
              </div>
            </SlideoutMenu.Footer>
          </>
        )}
      </SlideoutMenu>
    </SlideoutMenu.Trigger>
  )
}
